import React, { useState, useEffect } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import axios from "axios";
import Header from "../Header";
import { API_BASE_URL } from "../../config";

export default function Production() {
  const FIXED_RATE = 210;
  const CAKE_RATE = 25;
  const GROUNDNUT_RATE = 25;

  const [showPopup, setShowPopup] = useState(false);
  const [date, setDate] = useState(getTodayDateTime());
  const [records, setRecords] = useState([]);
  const [activeTab, setActiveTab] = useState("oil");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [products, setProducts] = useState({
    oil: { qt: "", amount: 0, rate: FIXED_RATE, unit: "Qt" },
    cake: { qt: "", amount: 0, rate: CAKE_RATE, unit: "Kg" },
    groundnut: { qt: "", amount: 0, rate: GROUNDNUT_RATE, unit: "Kg" },
  });

  const [bottles, setBottles] = useState({
    bottle_1l: { qt: "", rate: "", amount: 0 },
    bottle_5l: { qt: "", rate: "", amount: 0 },
    bottle_15l: { qt: "", rate: "", amount: 0 },
  });

  const PRODUCTS = [
    { id: "oil", name: "Oil", rate: FIXED_RATE, unit: "Qt" },
    { id: "cake", name: "Cake", rate: CAKE_RATE, unit: "Kg" },
    { id: "groundnut", name: "Groundnut", rate: GROUNDNUT_RATE, unit: "Kg" },
  ];

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const currentItems = records.slice(0, indexOfLastItem);
  const totalPages = Math.ceil(records.length / itemsPerPage);
  const hasMore = currentPage < totalPages;

  const loadMore = () => {
    setCurrentPage((prev) => prev + 1);
  };

  function getTodayDateTime() {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    return new Date(now.getTime() - offset * 60000).toISOString().slice(0, 16);
  }

  const handleQtChange = (e, productId) => {
    const value = e.target.value;
    const rate = products[productId].rate;
    const amount = (parseFloat(value || 0) * rate).toFixed(2);

    setProducts((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        qt: value,
        amount: parseFloat(amount),
      },
    }));
  };

  const handleBottleChange = (size, field, value) => {
    setBottles((prev) => {
      const updatedBottle = {
        ...prev[size],
        [field]:
          field === "qt" || field === "rate" ? parseFloat(value) || "" : value,
      };

      if (field === "qt" || field === "rate") {
        const qt = field === "qt" ? parseFloat(value) || 0 : prev[size].qt || 0;
        const rate =
          field === "rate" ? parseFloat(value) || 0 : prev[size].rate || 0;
        updatedBottle.amount = (qt * rate).toFixed(2);
      }

      return {
        ...prev,
        [size]: updatedBottle,
      };
    });
  };

  const handleSave = async () => {
    try {
      const totalAmount = (
        parseFloat(products.oil.amount || 0) +
        parseFloat(products.cake.amount || 0) +
        parseFloat(products.groundnut.amount || 0) +
        parseFloat(bottles.bottle_1l.amount || 0) +
        parseFloat(bottles.bottle_5l.amount || 0) +
        parseFloat(bottles.bottle_15l.amount || 0)
      ).toFixed(2);

      const requestData = {
        date,
        qt: parseFloat(products.oil.qt) || 0,
        rate: FIXED_RATE,
        amount: parseFloat(totalAmount),
        cake_qt: parseFloat(products.cake.qt) || 0,
        cake_amount: parseFloat(products.cake.amount) || 0,
        groundnut_qt: parseFloat(products.groundnut.qt) || 0,
        groundnut_amount: parseFloat(products.groundnut.amount) || 0,
        bottle_1l: parseFloat(bottles.bottle_1l.qt) || 0,
        bottle_5l: parseFloat(bottles.bottle_5l.qt) || 0,
        bottle_15l: parseFloat(bottles.bottle_15l.qt) || 0,
        bottle_1l_rate: parseFloat(bottles.bottle_1l.rate) || 0,
        bottle_5l_rate: parseFloat(bottles.bottle_5l.rate) || 0,
        bottle_15l_rate: parseFloat(bottles.bottle_15l.rate) || 0,
      };

      const res = await axios.post(
        `${API_BASE_URL}/api/productions`,
        requestData,
      );
      setRecords([res.data.data, ...records]);
      setShowPopup(false);
      resetForm();
    } catch (err) {
      console.error("Error saving data:", err);
      alert("Failed to save data. " + (err.response?.data?.message || ""));
    }
  };

  const resetForm = () => {
    setProducts({
      oil: { qt: "", amount: 0, rate: FIXED_RATE, unit: "Qt" },
      cake: { qt: "", amount: 0, rate: CAKE_RATE, unit: "Kg" },
      groundnut: { qt: "", amount: 0, rate: GROUNDNUT_RATE, unit: "Kg" },
    });
    setBottles({
      bottle_1l: { qt: "", rate: "", amount: 0 },
      bottle_5l: { qt: "", rate: "", amount: 0 },
      bottle_15l: { qt: "", rate: "", amount: 0 },
    });
    setDate(getTodayDateTime());
  };

  const fetchRecords = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/productions`);
      setRecords(res.data.data);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error fetching records:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    const confirmDelete = window.confirm(
      "Are you sure you want to remove this production entry?",
    );
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/productions/${id}`);
      setRecords((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error("Error deleting record:", err);
      alert("Failed to delete record");
    }
  };

  const groupByDay = (records) => {
    const grouped = {};
    records.forEach((record) => {
      const dateKey = new Date(record.date).toLocaleDateString("en-IN");
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(record);
    });
    return grouped;
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  return (
    <>
      <Header />
      <div className="container-fluid p-4" style={{ display: "inline-block" }}>
        <div className="container">
          <div className="mb-4 text-center">
            <Button variant="primary" onClick={() => setShowPopup(true)}>
              Add Production Entry
            </Button>
          </div>
          <div className="row">
            {Object.entries(groupByDay(currentItems)).map(
              ([day, dayRecords], index) => {
                const dayTotalQt = dayRecords.reduce(
                  (sum, rec) => sum + (parseFloat(rec.qt) || 0),
                  0,
                );
                const dayTotalAmount = dayRecords.reduce(
                  (sum, rec) => sum + (parseFloat(rec.amount) || 0),
                  0,
                );

                return (
                  <div key={index} className="mb-4">
                    <h5 className="p-3 mb-2 bg-white text-dark">
                      {day} — Subtotal: {dayTotalQt.toFixed(2)} Qt / ₹
                      {dayTotalAmount.toFixed(2)}
                    </h5>
                    <div className="row">
                      {dayRecords.map((record, idx) => (
                        <div className="col-md-4 mb-3" key={idx}>
                          <div className="card h-100">
                            <div className="card-body">
                              <h6 className="card-subtitle mb-2 text-muted">
                                {new Date(record.date).toLocaleString("en-IN", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })}
                              </h6>

                              <div className="mb-2">
                                <strong>Oil:</strong>{" "}
                                {parseFloat(record.qt || 0).toFixed(2)} Qt
                              </div>
                              <div className="mb-2">
                                <strong>Cake:</strong>{" "}
                                {parseFloat(record.cake_qt || 0).toFixed(2)} Kg
                              </div>
                              <div className="mb-2">
                                <strong>Groundnut:</strong>{" "}
                                {parseFloat(record.groundnut_qt || 0).toFixed(
                                  2,
                                )}{" "}
                                Kg
                              </div>
                              <div className="mb-2">
                                <strong>Bottles:</strong>
                                1L:{" "}
                                {parseFloat(record.bottle_1l || 0).toFixed(2)},
                                5L:{" "}
                                {parseFloat(record.bottle_5l || 0).toFixed(2)},
                                15L:{" "}
                                {parseFloat(record.bottle_15l || 0).toFixed(2)}
                              </div>
                              <div className="mt-3">
                                <strong>Total Amount:</strong> ₹
                                {parseFloat(record.amount || 0).toFixed(2)}
                              </div>

                              {localStorage.getItem("role") === "ADMIN" && (
                                <div className="mt-3 text-end">
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleDelete(record.id)}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              },
            )}
          </div>
          {/* Load More Button */}
          {hasMore && records.length > 0 && (
            <div className="text-center mb-5">
              <Button
                variant="outline-primary"
                onClick={loadMore}
                className="px-2"
              >
                Load More ({currentItems.length} of {records.length} shown)
              </Button>
            </div>
          )}
          <br /> <br /> <br />
          {/* Show when all items are loaded */}
          {!hasMore && records.length > 0 && (
            <div className="text-center text-muted mt-3">
              Showing all {records.length} records
            </div>
          )}
          {/* Show when no records */}
          {records.length === 0 && (
            <div className="text-center text-muted mt-5 py-5">
              <h4>No production records found</h4>
              <p>Click "Add Production Entry" to create a new record</p>
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        <Modal show={showPopup} onHide={() => setShowPopup(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Add Production Entry</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <div className="mb-3">
                <Form.Label>Date & Time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>

              <ul className="nav nav-tabs mb-3">
                {PRODUCTS.map((product) => (
                  <li className="nav-item" key={product.id}>
                    <button
                      className={`nav-link ${activeTab === product.id ? "active" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveTab(product.id);
                      }}
                    >
                      {product.name}
                    </button>
                  </li>
                ))}
              </ul>

              {PRODUCTS.map((product) => (
                <div
                  key={product.id}
                  className={`tab-pane ${activeTab === product.id ? "show active" : "d-none"}`}
                >
                  <Form.Group className="mb-3">
                    <Form.Label>Quantity ({product.unit})</Form.Label>
                    <Form.Control
                      type="number"
                      value={products[product.id].qt}
                      onChange={(e) => handleQtChange(e, product.id)}
                      placeholder={`Enter ${product.name} quantity`}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Amount (₹)</Form.Label>
                    <Form.Control
                      type="text"
                      value={products[product.id].amount}
                      readOnly
                    />
                  </Form.Group>
                </div>
              ))}

              <div className="card mt-3">
                <div className="card-header">
                  <h5>Bottle Stock</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    {["1l", "5l", "15l"].map((size) => (
                      <div className="col-md-4" key={size}>
                        <div className="mb-3">
                          <label className="form-label">
                            {size.toUpperCase()} Bottles
                          </label>
                          <div className="input-group mb-2">
                            <span className="input-group-text">Qty</span>
                            <input
                              type="number"
                              className="form-control"
                              value={bottles[`bottle_${size}`].qt}
                              onChange={(e) =>
                                handleBottleChange(
                                  `bottle_${size}`,
                                  "qt",
                                  e.target.value,
                                )
                              }
                              placeholder="Qty"
                            />
                          </div>
                          <div className="input-group mb-2">
                            <span className="input-group-text">₹</span>
                            <input
                              type="number"
                              className="form-control"
                              value={bottles[`bottle_${size}`].rate}
                              onChange={(e) =>
                                handleBottleChange(
                                  `bottle_${size}`,
                                  "rate",
                                  e.target.value,
                                )
                              }
                              placeholder="Rate"
                            />
                          </div>
                          <div className="input-group">
                            <span className="input-group-text">Amount</span>
                            <input
                              type="text"
                              className="form-control"
                              value={bottles[`bottle_${size}`].amount}
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPopup(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      <Header />
    </>
  );
}
